import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { IUser, MetricsType, WorkOrderData } from "DTO/userWorkOrderListDto";
import {
  assignWorkOrder,
  getAppConfig,
  getUsersList,
  getWorkerOrderbyId,
  getWorkOrderList,
  updateWorkOrderStatus,
} from "Services/Api";
import { AppConfType } from "types";

type SessionData = {
  workOrderListData: WorkOrderData[];
  setAllWorkorders: React.Dispatch<React.SetStateAction<WorkOrderData[]>>;
  appConfig: AppConfType | undefined;
  tenantUsers: Array<IUser> | undefined;
  currentWorkOrder: WorkOrderData | undefined;
  setCurrentWorkOrder: React.Dispatch<
    React.SetStateAction<WorkOrderData | undefined>
  >;
  metrics: MetricsType | undefined;
  isMobile: boolean;
  updateWorkOrderAssinge: (body: {
    case_id: string;
    new_assignee_username: string;
  }) => Promise<void>;
  changeWorkOrderStatus: (
    WOCaseId: string,
    newStatus: string,
    CaseAction?: string
  ) => Promise<boolean>;
};
export const Ctx = createContext<SessionData | undefined>(undefined);

export const useCtx = () => {
  const ctx = useContext(Ctx);
  if (ctx === undefined) {
    throw new Error("Context is undefined");
  }
  return ctx;
};
export const SessionDataContext = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [allWorkorders, setAllWorkorders] = useState<Array<WorkOrderData>>([]);
  const [appConfig, setAppConfig] = useState<AppConfType>();
  const [tenantUsers, setTenantUsers] = useState<Array<IUser>>();
  const [currentWorkOrder, setCurrentWorkOrder] =
    useState<WorkOrderData>(undefined);
  const [metrics, setMetrics] = useState<MetricsType>(undefined);

  const tenantsWorkOrders = useMemo(() => {
    return allWorkorders;
  }, [allWorkorders]);

  const isMobile = useMemo(() => {
    const regex =
      /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const mobile = regex.test(navigator.userAgent);
    return mobile;
  }, []);

  useEffect(() => {
    function checkStorage(e: StorageEvent) {
      if (e.key === "token") setAllWorkorders([]);
    }
    window.addEventListener("storage", checkStorage);
    return () => {
      window.removeEventListener("storage", checkStorage);
    };
  }, []);
  useEffect(() => {
    fetchAllWorkOrders();
    fetchAppConfig();
    fetchAllUsers();
    return () => {
      setAllWorkorders([]);
      setAppConfig(undefined);
      setTenantUsers(undefined);
    };
  }, []);

  const fetchAllUsers = useCallback(async () => {
    setTenantUsers((await getUsersList()).Users);
  }, []);
  const fetchAppConfig = async () => {
    setAppConfig(await getAppConfig());
  };
  const fetchAllWorkOrders = async () => {
    const workOrders =
      (await getWorkOrderList())
        //sorting table according to workorder id
        .sort(function (a, b) {
          return b.WOCaseId.localeCompare(a.WOCaseId);
        }) ?? [];
    setAllWorkorders(workOrders);
    setMetrics({
      AssignedCount: workOrders.filter((wO) => wO.Status === "assigned").length,
      UnassignedCount: workOrders.filter(
        (wO) => wO.Status === "unassigned" && wO.Type === "cable indication"
      ).length,
      OngoingProjectCount: workOrders.filter(
        (wO) => wO.Status !== "done" && wO.Type === "project"
      ).length,
    });
  };

  const updateWorkOrderAssinge = useCallback(
    async (body: { case_id: string; new_assignee_username: string }) => {
      const newAssinge = await assignWorkOrder(body);
      if (newAssinge) {
        const updatedWOrder = await getWorkerOrderbyId(body.case_id);
        setCurrentWorkOrder(updatedWOrder);
        setAllWorkorders((prev) =>
          prev.map((wO) =>
            wO.WOCaseId !== updatedWOrder.WOCaseId ? wO : updatedWOrder
          )
        );
      }
    },
    []
  );

  const changeWorkOrderStatus = useCallback(
    async (WOCaseId: string, newStatus: string, comment: string) => {
      const updatedStatus = await updateWorkOrderStatus(
        WOCaseId,
        newStatus,
        comment
      );

      if (updatedStatus) {
        setAllWorkorders((prev) =>
          prev.map((wO) => ({
            ...wO,
            Status: wO.WOCaseId === WOCaseId ? newStatus : wO.Status,
            Comment: wO.WOCaseId === WOCaseId ? comment : wO.Comment,
          }))
        );
        if (currentWorkOrder) {
          setCurrentWorkOrder({
            ...currentWorkOrder,
            Status:
              WOCaseId === currentWorkOrder.WOCaseId
                ? newStatus
                : currentWorkOrder.Status,
            Comment:
              WOCaseId === currentWorkOrder.WOCaseId
                ? comment
                : currentWorkOrder.Comment,
          });
        }
        return true;
      }
    },
    [currentWorkOrder]
  );

  const value = useMemo(
    () => ({
      workOrderListData: tenantsWorkOrders,
      setAllWorkorders,
      appConfig,
      tenantUsers,
      currentWorkOrder,
      setCurrentWorkOrder,
      metrics,
      isMobile,
      changeWorkOrderStatus,
      updateWorkOrderAssinge,
    }),
    [
      tenantsWorkOrders,
      setAllWorkorders,
      appConfig,
      tenantUsers,
      currentWorkOrder,
      setCurrentWorkOrder,
      metrics,
      isMobile,
      changeWorkOrderStatus,
      updateWorkOrderAssinge,
    ]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
